import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  FinanceAdminScreen,
  RbacType,
} from './screens/finance/finance-admin-screen.component';
import { AdminCreatePurchaseOrderScreen } from './screens/purchase-order/purchase-order-create-screen';
import { AdminOrdersScreen } from './screens/purchase-order/admin-purchases-screen.component';
import { AdminPurchaseOrderViewScreen } from './screens/purchase-order/admin-purchase-order-page.component';
import { DeliveryMethodScreen } from './screens/delivery/delivery-method-screen.component';
import { DeliveryOrderScreen } from './screens/delivery/delivery-order-screen';
import { PackageMethodScreen } from './screens/delivery/package-method-screen.component';
import { EmployeePurchaseOrdersTab } from './screens/purchase-order/employee-purchase-orders-tab';
import { PurchaseOrderDeliveryTariffsTab } from './screens/purchase-order/purchase-order-delivery-tariffs-tab.component';
import { AdminPurchaseOrdersTabs } from './screens/purchase-order/admin-purchase-orders.component';
import { DeliveryScreen } from './screens/delivery/delivery-screen';
import { DeliveryOrdersScreen } from './screens/delivery/delivery-orders-screen';
import { DeliveryMarkupsTab } from './screens/delivery/delivery-markups-tab.component';
import { PackageMethodsTab } from './screens/delivery/package-methods-tab.component';
import { DeliveryMethodsTab } from './screens/delivery/delivery-methods-tab.component';
import { DeliveryInsuranceTab } from './screens/delivery/delivery-insurance-tab.components';
import { AdminParcelCreateScreen } from './screens/parcels/admin-parcel-create-screen';
import { EmployeeParcelsTable } from './screens/parcels/employee-parcels-table';
import {
  AdminParcelItem,
  ParcelStatusProps,
} from './screens/parcels/parcel-item';
import { AdminParcelPage } from './screens/parcels/admin-parcel-page';
import { AdminMarketingScreen } from './screens/marketing/admin-marketing-screen';
import { MarketingStoriesTab } from './screens/marketing/marketing-stories-tab';
import { ClientExpansesBasedCommissionsCreateScreen } from './screens/purchase-order/client-expanses-based-commissions-create-screen';
import { ClientExpansesBasedCommissionsTableScreen } from './screens/purchase-order/client-expanses-based-commissions-table-screen';
import { ClientExpansesBasedCommissionsEditScreen } from './screens/purchase-order/client-expanses-based-commissions-edit-screen';
import * as Sentry from '@sentry/react';
import { EmployeeParcelsScreen } from './screens/parcels/employee-parcels-screen';
import { DeliveryOrdersTab } from './screens/delivery/delivery-orders-tab';
import { AuditLogScreen } from './screens/audit-log/audit-log-screen';
import { RbacObject } from '../export-types/cleaned-types';
import { Rbac, useRbac } from './common/Rbac';
import { RbacSome } from './common/Rbac';
import { AccessDenied } from './common/AccessDenied';
import { TransactionsHistoryTable } from './screens/finance/transactions-history-table.component';
import { TopUpAccounts } from './screens/finance/top-up-accounts';
import { LocalDeliveryMethodsTab } from './screens/delivery/local-delivery-methods-tab.component';
import { LocalDeliveryMethodScreen } from './screens/delivery/local-delivery-method-screen.component';
import { SuppliersLayout } from './screens/suppliers/suppliers-layout.component';
import { SuppliersTable } from './screens/suppliers/suppliers-table.component';
import { LocationsTable } from './screens/suppliers/locations-table.component';
import { UsersClientsTable } from './screens/users/users-clients-tab.component';
import { UsersEmployeesTable } from './screens/users/users-employees-tab.component';
import { UsersScreen } from './screens/users/users-screen.component';
import { AutoSendParametersScreen } from './screens/purchase-order/auto-send-parameters-screen';
import {
  CompositeDeliveryPropsStatus,
  CompositeDeliveriesScreen,
} from './screens/composite-delivery/composite-deliveries-screen';
import { CompositeDeliveriesTab } from './screens/composite-delivery/composite-delivery-tab';
import { CompositeDeliveryScreen } from './screens/composite-delivery/composite-delivery-screen';
import { ConversationStatus } from './screens/conversations/conversation-item';
import { ConversationScreen } from './screens/conversations/conversations-screen';
import { ConversationList } from './screens/conversations/conversation-list';
import { PromotionsTab } from './screens/marketing/promotions/promotions-tab';
import { DeliveryInsuranceScreen } from './screens/delivery/delivery-insurance-screen.components';
import { OtcRecommendationTab } from './screens/marketing/OtcRecommendationTab';
import { PoizonRecommendationTab } from './screens/marketing/PoizonRecommendationTab';
import { ServiceChargeOptionScreen } from './screens/purchase-order/service-charge-option-screen';
import { ServiceChargeOptionForm } from './screens/purchase-order/service-charge-option-form';
import { RefillTransactionsTable } from './screens/finance/refill-transactions-table.component';
import { WriteOffsTable } from './screens/finance/writeoffs-table.component';
import { CurrencyTable } from './screens/finance/currency-table.component';
import { DeliveryOrderCreateScreen } from './screens/delivery/delivery-order-create-screen.component';
import {
  userIsFinancier,
  userIsMarketer,
  userIsSupplierFinancier,
} from '../utils/common.utils';
import { StoreTab } from './screens/marketing/StoreTab';
import { AddStoreCard } from './screens/marketing/AddStoreCard';
import { BrandsTab } from './screens/marketing/BrandsTab';
import { AddBrand } from './screens/marketing/AddBrand';
import { useCurrentUser } from './current-user';
import { TransportationsTab } from './screens/delivery/transportations-tab';
import { DeliveryOrdersAssemblyTabs } from './screens/delivery/delivery-orders-assembly-tabs';
import { UtmsTab } from './screens/marketing/utms-tab';
import { EmailCampaignTab } from './screens/marketing/EmailCampaignTab';
import { FinanceDashboard } from './screens/finance/finance-admin-dashboard-screen.component';
import {
  AdminRelatedParcelAddExitedScreen,
  AdminRelatedParcelCreateScreen,
} from './screens/purchase-order/admin-related-parcel-create-screen';
import { PackagingTab } from './screens/delivery/packaging-tab.component';
import { TempAccessSessionTab } from './screens/users/temp-access-session/temp-access-session-tab';
import { FeatureFlagsTab } from './screens/users/feature-flags/feature-flags-tab';
import { BannersTab } from './screens/marketing/BannersTab';
import { CustomsDeliveryOrdersTab } from './screens/customs-delivery/customs-delivery-orders-tab';
import { CustomsDeliveriesSettingsTab } from './screens/customs-delivery/customs-deliveries-settings-tab';
import { CustomsDeliveriesScreen } from './screens/customs-delivery/customs-deliveries-screen';
import { CustomsDeliveryOrdersList } from './screens/customs-delivery/customs-delivery-orders-list';
import { CustomsDeliveryDutiesTab } from './screens/customs-delivery/duty/customs-duty-tab';
import { AdminCreateCustomsDeliveryOrderScreen } from './screens/customs-delivery/customs-delivery-order-create-screen';
import { AdminCustomsDeliveryOrderViewScreen } from './screens/customs-delivery/order/customs-delivery-screen';
import { CustomsDeliveryMethodsTab } from './screens/customs-delivery/methods/customs-delivery-methods-tab.component';
import { CustomsDeliveryMethodScreen } from './screens/customs-delivery/methods/customs-delivery-method-screen.component';
import { CustomsDeliveryOrdersInWorkTab } from './screens/customs-delivery/customs-delivery-orders-in-work-tab';
import { CustomsDeliveryOrdersDocumentsTab } from './screens/customs-delivery/customs-delivery-orders-documents-tab';
import { CustomsDeliverySupportsTab } from './screens/customs-delivery/support/customs-support-tab';
import { CustomsSupportCreateScreen } from './screens/customs-delivery/support/customs-support-create-screen';
import { RecommendationUtmsTab } from './screens/marketing/recommendation-utm/recommendation-utms-tab';
import { TransportationsArchiveTab } from './screens/delivery/transportations-archive-tab';
import { CustomsDeliveryTrueSignTab } from './screens/customs-delivery/true-sign/customs-true-sign-tab';
import { PromotionCommissionTab } from './screens/marketing/promotions/promotion-commission-tab';
import { PromotionBonusTab } from './screens/marketing/promotions/promotion-bonus-tab';
import { SmsComponent } from './screens/conversations/sms-component';
import { PromotionBurnBonusesLogsTab } from './screens/marketing/promotions/promotion-burn-bonuses-logs-tab';

const TransportationScreen = React.lazy(
  () => import('./screens/delivery/transportation-screen'),
);

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

type PurchaseRoute<Object extends RbacObject | undefined = undefined> = {
  path: string;
  status: string;
  rbac?: Object extends RbacObject ? RbacType<Object> : undefined;
};

const WFP: PurchaseRoute<RbacObject.Purchase> = {
  path: 'waiting-for-payment',
  status: 'waitingForPayment',
  rbac: { object: RbacObject.Purchase, action: 'read:extended-statuses' },
};

const HF: PurchaseRoute<RbacObject.Purchase> = {
  path: 'hidden-frozen',
  status: 'hiddenFrozen',
  rbac: { object: RbacObject.Purchase, action: 'read:hiddenPurchase' },
};

const purchaseRoutes = [
  WFP,
  { path: 'in-work', status: 'inWork' },
  { path: 'frozen', status: 'frozen' },
  HF,
  { path: 'on-the-way', status: 'onTheWayToStock' },
  { path: 'received-in-stock', status: 'receivedInStock' },
  { path: 'received-in-retail-stock', status: 'receivedInRetailStock' },
  { path: 'archive', status: 'archive' },
];

type ParcelRoute = {
  path: string;
  type: 'onTheWayToStock' | 'receivedInStock' | 'archive';
  status: ReadonlyArray<ParcelStatusProps>;
};
const parcelRoutes: ReadonlyArray<ParcelRoute> = [
  { path: 'on-the-way', type: 'onTheWayToStock', status: ['onTheWayToStock'] },
  {
    path: 'received-in-stock',
    type: 'receivedInStock',
    status: ['receivedInStock'],
  },
  { path: 'archive', type: 'archive', status: ['sentToRussia', 'canceled'] },
];

type ConversationRoute = {
  path: string;
  status: ConversationStatus | null;
};
const conversationRoutes: ReadonlyArray<ConversationRoute> = [
  { path: 'all', status: null },
  {
    path: 'refund',
    status: 'refund',
  },
  { path: 'unread', status: 'unread' },
  { path: 'defectCheck', status: 'defectCheck' },
  { path: 'delayed', status: 'delayed' },
  { path: 'adminAssistance', status: 'adminAssistance' },
  { path: 'archive', status: 'archive' },
];

type DeliveryRoute = {
  path: string;
  status: string;
};

type CustomsDeliveryRoute = {
  path: string;
  status: string;
};

const deliveryRoutes = [
  { path: 'processing', status: 'processing' as const },
  { path: 'retail', status: 'retail' as const },
  { path: 'received-in-russia', status: 'receivedInRussia' as const },
  {
    path: 'received-in-retail-russia',
    status: 'receivedInRetailRussia' as const,
  },
  { path: 'sent-to-recipient', status: 'sentToRecipient' as const },
  { path: 'delivered', status: 'delivered' as const },
] satisfies DeliveryRoute[];

const customsDeliveryRoutes = [
  { path: 'created', status: 'created' as const },
  { path: 'preparing', status: 'preparing' as const },
  { path: 'waitingForPayment', status: 'waitingForPayment' as const },
  { path: 'buyOut', status: 'buyOut' as const },
  { path: 'inWork', status: 'inWork' as const },
  { path: 'inStock', status: 'inStock' as const },
  { path: 'sentToRussia', status: 'sentToRussia' as const },
  { path: 'customs', status: 'customs' as const },
  { path: 'transitWarehouse', status: 'transitWarehouse' as const },
  {
    path: 'transferredToTC',
    status: 'transferredToTC' as const,
  },
  {
    path: 'received',
    status: 'received' as const,
  },
  { path: 'archived', status: 'archived' as const },
] satisfies CustomsDeliveryRoute[];

const customsDeliveryInWorkRoutes = [
  { path: 'codeSelected', status: 'codeSelected' as const },
  { path: 'calculationReady', status: 'calculationReady' as const },
  { path: 'protectCalculation', status: 'protectCalculation' as const },
] satisfies CustomsDeliveryRoute[];

const customsDeliveryDocumentsRoutes = [
  { path: 'prepareDocs', status: 'prepareDocs' as const },
  { path: 'prepareInvoice', status: 'prepareInvoice' as const },
  { path: 'declarant', status: 'declarant' as const },
] satisfies CustomsDeliveryRoute[];

type CompositeDeliveryRoute = {
  path: string;
  status: CompositeDeliveryPropsStatus;
};
const compositeDeliveryRoutes: ReadonlyArray<CompositeDeliveryRoute> = [
  { path: 'assembly', status: 'assembly' },
  {
    path: 'sent-to-russia',
    status: 'sentToRussia',
  },
  { path: 'received-in-russia', status: 'receivedInRussia' },
];

export const UnifiedAppRouter = () => {
  const currentUser = useCurrentUser();
  const { hasPermission } = useRbac();

  return (
    <SentryRoutes>
      <Route
        path="/users"
        element={
          <Rbac
            object={RbacObject.User}
            action={'read:admin'}
            otherwise={() => <AccessDenied />}
          >
            <UsersScreen />
          </Rbac>
        }
      >
        <Route index element={<Navigate to="/users/clients" replace />} />
        <Route path="/users/clients" element={<UsersClientsTable />} />
        <Route path="/users/employees" element={<UsersEmployeesTable />} />
        <Route
          path="/users/temp-access-sessions/all"
          element={<TempAccessSessionTab status={'all'} />}
        />
        <Route
          path="/users/temp-access-sessions/active"
          element={<TempAccessSessionTab status={'active'} />}
        />
        <Route
          path="/users/temp-access-sessions/plan"
          element={<TempAccessSessionTab status={'plan'} />}
        />
        <Route
          path="/users/temp-access-sessions/archived"
          element={<TempAccessSessionTab status={'archived'} />}
        />
        <Route
          path="/users/feature-flags"
          element={
            <Rbac
              object={RbacObject.FeatureFlag}
              action={'read:admin'}
              otherwise={() => <AccessDenied />}
            >
              <FeatureFlagsTab />
            </Rbac>
          }
        />
      </Route>
      <Route
        path="/finances-admin"
        element={
          <Rbac
            object={RbacObject.Transaction}
            action={['read:admin', 'read:supplier']}
            otherwise={() => <AccessDenied />}
          >
            <FinanceAdminScreen />
          </Rbac>
        }
      >
        <Route
          path="/finances-admin/history"
          element={<TransactionsHistoryTable />}
        />
        <Route path="/finances-admin/finances" element={<FinanceDashboard />} />
        <Route
          index
          element={<Navigate to="/finances-admin/refills" replace />}
        />
        <Route
          path="/finances-admin/refills"
          element={<RefillTransactionsTable />}
        />
        <Route path="/finances-admin/write-offs" element={<WriteOffsTable />} />
        <Route
          path="/finances-admin/currency"
          element={
            <Rbac
              object={RbacObject.Currency}
              action={'read'}
              otherwise={() => <AccessDenied />}
            >
              <CurrencyTable />
            </Rbac>
          }
        />
        <Route
          path="/finances-admin/top-up-accounts"
          element={
            <Rbac
              object={RbacObject.TopUpMethod}
              action={'read:accounts'}
              otherwise={() => <AccessDenied />}
            >
              <TopUpAccounts />
            </Rbac>
          }
        />
      </Route>
      <Route
        path="/audit-log"
        element={
          <Rbac
            object={RbacObject.AuditLog}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <AuditLogScreen />
          </Rbac>
        }
      />

      <Route
        path={'/customs-deliveries/delivery-methods/create'}
        element={
          <Rbac
            object={RbacObject.CustomsDelivery}
            action={'write:settings'}
            otherwise={() => <AccessDenied />}
          >
            <CustomsDeliveryMethodScreen />
          </Rbac>
        }
      />
      <Route
        path={'/customs-deliveries/delivery-supports/create'}
        element={
          <Rbac
            object={RbacObject.CustomsDelivery}
            action={'write:settings'}
            otherwise={() => <AccessDenied />}
          >
            <CustomsSupportCreateScreen />
          </Rbac>
        }
      />
      <Route
        path="/customs-deliveries/order/:id"
        element={
          <RbacSome
            checks={[{ object: RbacObject.CustomsDelivery, action: 'read' }]}
            otherwise={() => <AccessDenied />}
          >
            <AdminCustomsDeliveryOrderViewScreen />
          </RbacSome>
        }
      />
      <Route
        path={'/customs-deliveries/create'}
        element={
          <Rbac
            object={RbacObject.CustomsDelivery}
            action={'write'}
            otherwise={() => <AccessDenied />}
          >
            <AdminCreateCustomsDeliveryOrderScreen />
          </Rbac>
        }
      />
      <Route
        path={'/customs-deliveries'}
        element={
          <Rbac
            object={RbacObject.CustomsDelivery}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <CustomsDeliveriesScreen />
          </Rbac>
        }
      >
        <Route
          index
          element={<Navigate to="/customs-deliveries/orders" replace />}
        />

        <Route
          path="/customs-deliveries/delivery-methods"
          element={
            <Rbac
              object={RbacObject.CustomsDelivery}
              action={'read:settings'}
              otherwise={() => <AccessDenied />}
            >
              <CustomsDeliveryMethodsTab />
            </Rbac>
          }
        />
        <Route
          path="/customs-deliveries/delivery-supports"
          element={
            <Rbac
              object={RbacObject.CustomsDelivery}
              action={'read:settings'}
              otherwise={() => <AccessDenied />}
            >
              <CustomsDeliverySupportsTab />
            </Rbac>
          }
        />

        <Route
          path={'/customs-deliveries/settings'}
          element={
            <Rbac
              object={RbacObject.CustomsDelivery}
              action={'read:settings'}
              otherwise={() => <AccessDenied />}
            >
              <CustomsDeliveriesSettingsTab />
            </Rbac>
          }
        />
        <Route path={'/customs-deliveries/duties'}>
          <Route
            index
            element={<Navigate to="/customs-deliveries/duties/price" replace />}
          />
          <Route
            path={'/customs-deliveries/duties/price'}
            element={
              <Rbac
                object={RbacObject.CustomsDelivery}
                action={'read:settings'}
                otherwise={() => <AccessDenied />}
              >
                <CustomsDeliveryDutiesTab />
              </Rbac>
            }
          />
          <Route
            path={'/customs-deliveries/duties/code'}
            element={
              <Rbac
                object={RbacObject.CustomsDelivery}
                action={'read:settings'}
                otherwise={() => <AccessDenied />}
              >
                <CustomsDeliveryDutiesTab />
              </Rbac>
            }
          />
        </Route>

        <Route path={'/customs-deliveries/true-sign'}>
          <Route
            index
            element={
              <Navigate to="/customs-deliveries/true-sign/for-apply" replace />
            }
          />
          <Route
            path={'/customs-deliveries/true-sign/for-apply'}
            element={
              <Rbac
                object={RbacObject.CustomsDelivery}
                action={'read:settings'}
                otherwise={() => <AccessDenied />}
              >
                <CustomsDeliveryTrueSignTab />
              </Rbac>
            }
          />
          <Route
            path={'/customs-deliveries/true-sign/for-order'}
            element={
              <Rbac
                object={RbacObject.CustomsDelivery}
                action={'read:settings'}
                otherwise={() => <AccessDenied />}
              >
                <CustomsDeliveryTrueSignTab />
              </Rbac>
            }
          />
        </Route>
        <Route
          path="/customs-deliveries/orders"
          element={
            <Rbac
              object={RbacObject.CustomsDelivery}
              action={'read'}
              otherwise={() => <AccessDenied />}
            >
              <CustomsDeliveryOrdersTab />
            </Rbac>
          }
        >
          <Route
            index
            element={
              <Navigate to="/customs-deliveries/orders/preparing" replace />
            }
          />
          <Route
            path={'inWorkPreparing'}
            element={<CustomsDeliveryOrdersInWorkTab />}
          >
            <Route
              index
              element={
                <Navigate
                  to="/customs-deliveries/orders/inWorkPreparing/codeSelected"
                  replace
                />
              }
            />

            {customsDeliveryInWorkRoutes.map(({ path, status }) => (
              <Route
                path={path}
                key={path}
                element={<CustomsDeliveryOrdersList status={status} />}
              />
            ))}
          </Route>
          <Route
            path={'documents'}
            element={<CustomsDeliveryOrdersDocumentsTab />}
          >
            <Route
              index
              element={
                <Navigate
                  to="/customs-deliveries/orders/documents/prepareDocs"
                  replace
                />
              }
            />
            {customsDeliveryDocumentsRoutes.map(({ path, status }) => (
              <Route
                path={path}
                key={path}
                element={<CustomsDeliveryOrdersList status={status} />}
              />
            ))}
          </Route>
          {customsDeliveryRoutes.map(({ path, status }) => (
            <Route
              path={path}
              key={path}
              element={<CustomsDeliveryOrdersList status={status} />}
            />
          ))}
        </Route>
      </Route>

      <Route
        path="/deliveries/delivery-method"
        element={
          <Rbac
            object={RbacObject.DeliveryMethod}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <DeliveryMethodScreen />
          </Rbac>
        }
      />
      <Route
        path="/deliveries/local-delivery-method"
        element={
          <Rbac
            object={RbacObject.LocalDeliveryMethod}
            action={'write'}
            otherwise={() => <AccessDenied />}
          >
            <LocalDeliveryMethodScreen />
          </Rbac>
        }
      />
      <Route
        path="/deliveries/package-method"
        element={
          <Rbac
            object={RbacObject.PackageMethod}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <PackageMethodScreen />
          </Rbac>
        }
      />
      <Route
        path="/deliveries/delivery-insurance"
        element={
          <Rbac
            object={RbacObject.DeliveryInsurance}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <DeliveryInsuranceScreen />
          </Rbac>
        }
      />
      <Route
        path="/deliveries/delivery-order-create"
        element={
          <Rbac
            object={RbacObject.DeliveryOrder}
            action={'write'}
            otherwise={() => <AccessDenied />}
          >
            <DeliveryOrderCreateScreen />
          </Rbac>
        }
      />
      <Route
        path="/marketing"
        element={
          <Rbac
            object={RbacObject.Story}
            action={'read:admin'}
            otherwise={() => <AccessDenied />}
          >
            <AdminMarketingScreen />
          </Rbac>
        }
      >
        <Route index element={<Navigate to="stories" replace />} />
        <Route path="stories" element={<MarketingStoriesTab />} />
        <Route path="promotions" element={<PromotionsTab />}>
          <Route index element={<Navigate to="commission" replace />} />
          <Route path="bonus" element={<PromotionBonusTab />} />
          <Route path="commission" element={<PromotionCommissionTab />} />
          <Route
            path={'burn-bonuses-logs'}
            element={<PromotionBurnBonusesLogsTab />}
          />
        </Route>
        <Route path="otcRecommendation" element={<OtcRecommendationTab />} />
        <Route
          path="poizonRecommendation"
          element={<PoizonRecommendationTab />}
        />
        <Route path="store" element={<StoreTab />} />
        <Route path="brands" element={<BrandsTab />} />
        <Route path="analytic" element={<UtmsTab />} />
        <Route
          path={'recommendation-utm'}
          element={<RecommendationUtmsTab />}
        />
        <Route path="email" element={<EmailCampaignTab />} />
        <Route path="banner" element={<BannersTab />} />
      </Route>
      <Route
        path="/store/card"
        element={
          <Rbac
            object={RbacObject.StoreProductCard}
            action={'read:admin'}
            otherwise={() => <AccessDenied />}
          >
            <AddStoreCard />
          </Rbac>
        }
      ></Route>
      <Route
        path="/brands/brand"
        element={
          <Rbac
            object={RbacObject.Story}
            action={'read:admin'}
            otherwise={() => <AccessDenied />}
          >
            <AddBrand />
          </Rbac>
        }
      ></Route>

      <Route
        path="/purchases/commissions/create"
        element={
          <Rbac
            object={RbacObject.PurchaseOrderClientExpensesPurchaseMarkup}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <ClientExpansesBasedCommissionsCreateScreen />
          </Rbac>
        }
      />
      <Route
        path="/purchases/commissions/:id"
        element={
          <Rbac
            object={RbacObject.PurchaseOrderClientExpensesPurchaseMarkup}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <ClientExpansesBasedCommissionsEditScreen />
          </Rbac>
        }
      />
      <Route
        path="/purchases/form"
        element={
          <Rbac
            object={RbacObject.Purchase}
            action={'write:admin-create'}
            otherwise={() => <AccessDenied />}
          >
            <AdminCreatePurchaseOrderScreen />
          </Rbac>
        }
      />
      <Route
        path="/purchases/parcel-form"
        element={
          <Rbac
            object={RbacObject.Parcel}
            action={'write:admin-create'}
            otherwise={() => <AccessDenied />}
          >
            <AdminParcelCreateScreen />
          </Rbac>
        }
      />

      <Route
        path="/purchases/related-parcel-form/:id"
        element={
          <Rbac
            object={RbacObject.Parcel}
            action={'write:related-parcel'}
            otherwise={() => <AccessDenied />}
          >
            <AdminRelatedParcelCreateScreen />
          </Rbac>
        }
      />

      <Route
        path="/purchases/related-parcel-form/:id/add-existed"
        element={
          <Rbac
            object={RbacObject.Parcel}
            action={'write:related-parcel'}
            otherwise={() => <AccessDenied />}
          >
            <AdminRelatedParcelAddExitedScreen />
          </Rbac>
        }
      />

      <Route
        path="/deliveries/delivery-order/:id"
        element={
          <RbacSome
            checks={[
              { object: RbacObject.DeliveryOrder, action: 'read' },
              { object: RbacObject.DeliveryOrder, action: 'read:transit' },
            ]}
            otherwise={() => <AccessDenied />}
          >
            <DeliveryOrderScreen />
          </RbacSome>
        }
      />
      <Route
        path="/composite-deliveries/:id"
        element={
          <Rbac
            object={RbacObject.CompositeDeliveryOrder}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <CompositeDeliveryScreen />
          </Rbac>
        }
      />
      <Route
        path="/composite-deliveries"
        element={
          <Rbac
            object={RbacObject.CompositeDeliveryOrder}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <CompositeDeliveriesScreen />
          </Rbac>
        }
      >
        <Route
          index
          element={<Navigate to="/composite-deliveries/assembly" replace />}
        />
        {compositeDeliveryRoutes.map(({ path, status }) => (
          <Route
            path={path}
            key={path}
            element={<CompositeDeliveriesTab key={path} status={status} />}
          />
        ))}
      </Route>
      <Route
        path="/deliveries"
        element={
          <RbacSome
            checks={[
              { object: RbacObject.DeliveryOrder, action: 'read' },
              { object: RbacObject.DeliveryOrder, action: 'read:transit' },
            ]}
            otherwise={() => <AccessDenied />}
          >
            <DeliveryScreen />
          </RbacSome>
        }
      >
        <Route index element={<Navigate to="/deliveries/orders" replace />} />
        <Route path="/deliveries/orders" element={<DeliveryOrdersScreen />}>
          <Route
            index
            element={
              <Navigate
                to={
                  hasPermission(RbacObject.DeliveryOrder, 'read')
                    ? '/deliveries/orders/assembly'
                    : '/deliveries/orders/sent-to-russia'
                }
                replace
              />
            }
          />
          <Route path={'assembly'} element={<DeliveryOrdersAssemblyTabs />}>
            <Route index element={<Navigate to="./standalone" replace />} />
            <Route
              path={'standalone'}
              element={
                <TransportationsTab status="created" type="standalone" />
              }
            />
            <Route
              path={'combined'}
              element={<TransportationsTab status="created" type="combined" />}
            />
            <Route path={'packaged'} element={<PackagingTab />} />
          </Route>
          <Route
            path={'sent-to-russia'}
            element={<TransportationsTab status="sent" />}
          />
          <Route path={'archive'} element={<TransportationsArchiveTab />} />
          {deliveryRoutes.map(({ path, status }) => (
            <Route
              path={path}
              key={path}
              element={<DeliveryOrdersTab status={status} />}
            />
          ))}
        </Route>

        <Route
          path="/deliveries/delivery"
          element={
            <Rbac
              object={RbacObject.DeliveryMethod}
              action={'read'}
              otherwise={() => <AccessDenied />}
            >
              <DeliveryMethodsTab />
            </Rbac>
          }
        />
        <Route
          path="/deliveries/package"
          element={
            <Rbac
              object={RbacObject.PackageMethod}
              action={'read'}
              otherwise={() => <AccessDenied />}
            >
              <PackageMethodsTab />
            </Rbac>
          }
        />
        <Route
          path="/deliveries/markups"
          element={
            <Rbac
              object={RbacObject.DeliveryOrderDeliveryMarkup}
              action={'read'}
              otherwise={() => <AccessDenied />}
            >
              <DeliveryMarkupsTab />
            </Rbac>
          }
        />
        <Route
          path="/deliveries/local-delivery"
          element={
            <Rbac
              object={RbacObject.LocalDeliveryMethod}
              action={'read'}
              otherwise={() => <AccessDenied />}
            >
              <LocalDeliveryMethodsTab />
            </Rbac>
          }
        />
        <Route
          path="/deliveries/insurance"
          element={
            <Rbac
              object={RbacObject.DeliveryInsurance}
              action={'read'}
              otherwise={() => <AccessDenied />}
            >
              <DeliveryInsuranceTab />
            </Rbac>
          }
        />
      </Route>

      <Route
        path="/parcels/:id"
        element={
          <Rbac
            object={RbacObject.Parcel}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <AdminParcelPage />
          </Rbac>
        }
      />
      <Route
        path="/parcels"
        element={
          <Rbac
            object={RbacObject.Parcel}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <EmployeeParcelsScreen />
          </Rbac>
        }
      >
        <Route index element={<Navigate to="/parcels/on-the-way" replace />} />
        {parcelRoutes.map(({ path, type, status }) => (
          <Route
            path={path}
            key={path}
            element={
              <EmployeeParcelsTable
                render={(props) => (
                  <AdminParcelItem key={props.data.id} {...props} />
                )}
                key={path}
                type={type}
                status={status}
              />
            }
          />
        ))}
      </Route>
      {/*костыль для сотрудников: после рефакторинга поменялись роуты */}
      {purchaseRoutes.map(({ path }) => (
        <Route
          key={path}
          path={`/purchases/${path}`}
          element={<Navigate to={`/purchases/orders/${path}`} replace />}
        />
      ))}
      <Route
        path="/purchases/:id"
        element={
          <Rbac
            object={RbacObject.Purchase}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <AdminPurchaseOrderViewScreen />
          </Rbac>
        }
      />
      <Route
        path="/purchases"
        element={
          <Rbac
            object={RbacObject.Purchase}
            action={'read'}
            otherwise={() => <AccessDenied />}
          >
            <AdminOrdersScreen />
          </Rbac>
        }
      >
        <Route index element={<Navigate to="/purchases/orders" replace />} />
        <Route path="/purchases/orders" element={<AdminPurchaseOrdersTabs />}>
          <Route
            index
            element={<Navigate to="/purchases/orders/in-work" replace />}
          />
          {purchaseRoutes.map(({ path, status, rbac }) => {
            const element = (
              <EmployeePurchaseOrdersTab key={status} type={status} />
            );

            return (
              <Route
                path={path}
                key={path}
                element={
                  rbac ? (
                    <Rbac
                      key={path}
                      object={rbac.object}
                      action={rbac.action}
                      otherwise={() => <AccessDenied />}
                    >
                      {element}
                    </Rbac>
                  ) : (
                    element
                  )
                }
              />
            );
          })}
        </Route>

        <Route
          path="/purchases/tariffs"
          key={'tariffs'}
          element={
            <Rbac
              object={RbacObject.PurchaseOrderDeliveryTariff}
              action={'write'}
              otherwise={() => <AccessDenied />}
            >
              <PurchaseOrderDeliveryTariffsTab />
            </Rbac>
          }
        />
        <Route
          path="/purchases/commissions"
          element={
            <Rbac
              object={RbacObject.PurchaseOrderClientExpensesPurchaseMarkup}
              action={'read'}
              otherwise={() => <AccessDenied />}
            >
              <ClientExpansesBasedCommissionsTableScreen />
            </Rbac>
          }
        />
        <Route
          path="/purchases/auto-send"
          element={
            <Rbac
              object={RbacObject.AutoSendParameters}
              action={'read'}
              otherwise={() => <AccessDenied />}
            >
              <AutoSendParametersScreen />
            </Rbac>
          }
        />
        <Route
          path="/purchases/service-charge-option"
          element={
            <Rbac
              object={RbacObject.ServiceChargeOption}
              action={'read:admin'}
              otherwise={() => <AccessDenied />}
            >
              <ServiceChargeOptionScreen />
            </Rbac>
          }
        />
      </Route>
      <Route
        path="/purchases/service-charge-option/form"
        element={
          <Rbac
            object={RbacObject.ServiceChargeOption}
            action={'write:admin'}
            otherwise={() => <AccessDenied />}
          >
            <ServiceChargeOptionForm />
          </Rbac>
        }
      />
      <Route
        path="/suppliers"
        element={
          <Rbac
            object={RbacObject.Supplier}
            action={'read:admin'}
            otherwise={() => <AccessDenied />}
          >
            <SuppliersLayout />
          </Rbac>
        }
      >
        <Route index element={<Navigate to="list" replace />} />
        <Route path="list" element={<SuppliersTable />} />
        <Route path="location" element={<LocationsTable />} />
      </Route>

      <Route
        path="*"
        element={
          currentUser.isAdmin ? (
            <Navigate to={'/purchases'} replace />
          ) : userIsFinancier(currentUser.profile) ? (
            <Navigate to={'/finances-admin'} replace />
          ) : userIsMarketer(currentUser.profile) ? (
            <Navigate to={'/marketing'} replace />
          ) : userIsSupplierFinancier(currentUser.profile) ? (
            <Navigate to={'/finances-admin/refills'} replace />
          ) : (
            <Navigate to={'/deliveries'} replace />
          )
        }
      />
      <Route path="/conversations" element={<ConversationScreen />}>
        <Route index element={<Navigate to="/conversations/all" replace />} />
        {conversationRoutes.map(({ path, status }) => (
          <Route
            path={path}
            key={path}
            element={<ConversationList key={path} status={status} />}
          />
        ))}
        <Route path="sms" element={<SmsComponent />} />
      </Route>
      <Route
        path="/transportations/:id"
        element={
          <Rbac
            object={RbacObject.Transportation}
            action={['read']}
            otherwise={() => <AccessDenied />}
          >
            <Suspense>
              <TransportationScreen />
            </Suspense>
          </Rbac>
        }
      />
    </SentryRoutes>
  );
};
